import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { isBrowser, isSmallScreen } from "../helpers/screens";
import { useAuth, useOrganizationList } from "@clerk/clerk-react";
import { useLocation, useNavigate } from "react-router";
import { useJwt } from "../helpers/common";


interface SidebarContextProps {
  isOpenOnSmallScreens: boolean;
  isPageWithSidebar: boolean;
  // eslint-disable-next-line no-unused-vars
  setOpenOnSmallScreens: (isOpen: boolean) => void;
}

const SideNavContext = createContext<SidebarContextProps>(undefined!);

const  SideNavProvider = ({ children }: PropsWithChildren) => {
  const location = isBrowser() ? window.location.pathname : "/";
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    if(window.localStorage.getItem("isSidebarOpen")){
      window.localStorage.setItem("isSidebarOpen", "true");
      setOpen(isBrowser() ? true : false)
    } else {
      setOpen(isBrowser() ? window.localStorage.getItem("isSidebarOpen") === "true" : false)
    }
  }, []);

  // Save latest state to localStorage
  useEffect(() => {
    window.localStorage.setItem("isSidebarOpen", isOpen.toString());
  }, [isOpen]);

  // Close Sidebar on page change on mobile
  useEffect(() => {
    if (isSmallScreen()) {
      setOpen(false);
    }
  }, [location]);

  // Close Sidebar on mobile tap inside main content
  useEffect(() => {
    function handleMobileTapInsideMain(event: MouseEvent) {
      const main = document.querySelector("main");
      const isClickInsideMain = main?.contains(event.target as Node);

      if (isSmallScreen() && isClickInsideMain) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleMobileTapInsideMain);
    return () => {
      document.removeEventListener("mousedown", handleMobileTapInsideMain);
    };
  }, []);

  const { isLoaded, isSignedIn } = useAuth();
  const { JWTData, tokenLoaded } = useJwt(true);
  const { setActive } = useOrganizationList();
  const { org_id = "", org_list = [] } = JWTData;
  const navigate = useNavigate();
  const locationn = useLocation();

  useEffect(() => {
    if (!isLoaded) return;

    const navigateToProjectsPage = () => {
      if(locationn.pathname === "/signup") {
        navigate("/projects")
      }
    }

    const fetchdata = async () => {
      if(isSignedIn 
        && Object.keys(JWTData).length > 0
      ) {
        if (!org_id) {
          if(Object.keys(org_list).length){
            const [firstOrgId = ''] = Object.keys(org_list)
            if(firstOrgId && typeof setActive == "function"){
              await setActive({ organization: firstOrgId })
              navigateToProjectsPage()
            } else {
              navigate("/signup");
            }
          } else {
            navigate("/signup");
          }
        } else {
          navigateToProjectsPage()
        }
      }
    }

    fetchdata();
  }, [isLoaded, isSignedIn, JWTData, tokenLoaded]);

  if(isSignedIn) {
    const ret = null;
    // scenarios when to show null
    // when I am on projects and I dont have orgId then go to signup
      // When I am on signup and I dont have orgId then render form
    // when i am on signup and I have orgId then go to projects 
    // when i am on signup and I dont have orgId then show null
    // Also when token loading show null
    if(locationn.pathname === "/signup") {
      if(tokenLoaded) {
        if (org_id) {
          return ret;
          // return <div className="text-white">On Signup page with org id</div>;
        }
      } else {
        // return <div className="text-white">Token loading</div>;
        return ret;
      }
    } else if(locationn.pathname === "/projects" || locationn.pathname === "/") {
      if(tokenLoaded) {
        if (!org_id) {
          return ret;
          // return <div className="text-white">On Non Signup page without org id</div>;
        }
      } else {
        // return <div className="text-white">Token loading</div>;
        return ret;
      }
    }
  }

  return (
    <SideNavContext.Provider
      value={{
        isOpenOnSmallScreens: isOpen,
        isPageWithSidebar: true,
        setOpenOnSmallScreens: setOpen,
      }}
    >
      {children}
    </SideNavContext.Provider>
  );
}
export default SideNavProvider;

export function useSideNavContext(): SidebarContextProps {
  const context = useContext(SideNavContext);

  if (typeof context === "undefined") {
    throw new Error(
      "useSidebarContext should be used within the SidebarContext provider!"
    );
  }

  return context;
}